import { Site } from '@envato/foundation-types'

import { hasFeature } from '../../common/utils/features'
import { ICommonLocation } from '../../common/utils/location/types'
import { IExperimentsState } from '../../common/reducers/experiments'
import { IRootState } from '../../common/reducers'
import { IPageBlock } from '../../typings/pageBlocks'

import {
  decorateSearchResult,
  DecorateSearchResultFunction
} from '../../common/selectors/searchResults'
import { blockContainsItems } from '../../common/actions/common'

const isScheduledForNow = (block: IPageBlock<any>) => {
  if (block.scheduled) {
    const currentDate = new Date()
    const startDate = new Date(block.scheduled.startsAt)
    const endDate = new Date(block.scheduled.endsAt)
    return startDate < currentDate && currentDate < endDate
  } else {
    return false
  }
}

export function isNotEmpty<T>(component: T | null): component is T {
  return !!component
}

export const shouldShowBlock = ({
  block,
  currentSite,
  location,
  permitScheduledBlocks
}: {
  block: IPageBlock<any>
  currentSite: Site
  location: ICommonLocation
  permitScheduledBlocks: boolean
}): boolean => {
  const shouldShowAllScheduled = hasFeature(
    'showAllScheduledBlocks',
    currentSite,
    location
  )

  const hasNoSchedule = !block.scheduled
  const shouldShowBasedOnSchedule =
    hasNoSchedule ||
    (permitScheduledBlocks && isScheduledForNow(block)) ||
    shouldShowAllScheduled

  return shouldShowBasedOnSchedule
}

export const decorateBlocks = (
  state: IRootState,
  blocks: IPageBlock[] = [],
  decorateFunc: DecorateSearchResultFunction = decorateSearchResult
) => {
  return blocks.map(block => {
    if (blockContainsItems(block)) {
      return {
        ...block,
        data: block.data.map(item => decorateFunc(state, item))
      }
    } else {
      return block
    }
  })
}

export const selectBlocksFromActiveFeatureFlip = ({
  page,
  data,
  currentSite,
  location,
  experiments
}: {
  page: string
  data: {
    blocks: IPageBlock[]
    blocksB: IPageBlock[]
    blocksC: IPageBlock[]
    blocksD: IPageBlock[]
  }
  currentSite: Site
  location: ICommonLocation
  experiments: IExperimentsState
}): IPageBlock[] => {
  const featureToBlocksMap = {
    [`${page}PageBlocksB`]: data.blocksB,
    [`${page}PageBlocksC`]: data.blocksC,
    [`${page}PageBlocksD`]: data.blocksD
  }

  let blocks = data.blocks

  for (const key in featureToBlocksMap) {
    if (hasFeature(key, currentSite, location, experiments)) {
      blocks = featureToBlocksMap[key]
    }
  }

  // in case of emergency this allows us to hide the campaign
  // using only a feature flip change in the config
  if (!hasFeature('saleCampaign', currentSite, location, experiments)) {
    blocks = blocks.filter(
      ({ componentType }) => !['FullWidthBannerBlock'].includes(componentType)
    )
  }

  return blocks
}
